import React from 'react';
import { LegalLayout, DefinitionTable } from '../components/ui/LegalLayout';

/**
 * 特定商取引法に基づく表記。
 *
 * 【重要】ここに書く内容はお客様に対する法的な約束になる。
 * 価格・支払方法・返金条件・引渡時期を変更するときは必ず依頼元の確認を取ること。
 * とくにクレジットカード決済は、実際に決済手段を導入してから記載を有効にする。
 */
const ROWS: Array<{ label: string; value: React.ReactNode }> = [
  { label: '販売事業者', value: 'オーアーク株式会社（MYOAKU Co., Ltd.）' },
  { label: '運営統括責任者', value: '徐 科（XU KE）' },
  {
    label: '所在地',
    value: (
      <>
        〒132-0013
        <br />
        東京都江戸川区江戸川2丁目30-6 小倉荘201号室
      </>
    ),
  },
  {
    label: '電話番号・メールアドレス',
    value: (
      <>
        ご請求いただいた場合、遅滞なく開示いたします。
        <br />
        <span className="text-body-faint">
          （<a href="/monitor.html#form-head" className="text-brand-600 underline underline-offset-4">お申し込みフォーム</a>の「ご質問・ご要望」欄よりご請求ください）
        </span>
      </>
    ),
  },
  { label: 'ホームページURL', value: 'https://sakumie.jp' },
  {
    label: '販売価格',
    value: (
      <>
        サクミエ会計 スタンダードプラン
        <br />
        年額一括払い：<span className="num font-bold text-ink-900">5,900円</span>（税込）
        <br />
        月額払い：<span className="num font-bold text-ink-900">980円</span>（税込）
        <br />
        <span className="text-body-faint">
          ※ 初期費用は無料です。価格は本サイト上に表示された金額に準じます。
        </span>
      </>
    ),
  },
  {
    label: '商品代金以外に必要な費用',
    value: (
      <>
        銀行振込をご利用の場合の振込手数料はお客様のご負担となります。
        <br />
        本製品のダウンロードおよびご利用にあたって必要となる
        インターネット接続料金・通信料金はお客様のご負担となります。
      </>
    ),
  },
  {
    label: 'お支払い方法',
    value: (
      <>
        銀行振込／クレジットカード決済
        <br />
        <span className="text-body-faint">
          ※ ご利用可能な決済手段は、お申し込み時に個別にご案内いたします。
        </span>
      </>
    ),
  },
  {
    label: 'お支払い時期',
    value: (
      <>
        銀行振込：当社発行の請求書に記載された期日（請求書発行日より14日以内）までにお支払いください。
        <br />
        クレジットカード：お申し込み手続き時にお支払いが確定します。
        引き落とし日はご利用のカード会社の規定によります。
      </>
    ),
  },
  {
    label: '商品の引渡時期',
    value: (
      <>
        お支払いの確認後、5営業日以内に、
        ダウンロードに必要な情報をご登録のメールアドレス宛にご案内いたします。
        <br />
        <span className="text-body-faint">
          ※ 30日間無料体験をお申し込みの場合は、
          お申し込みから2営業日以内に担当者よりご連絡し、セットアップをご案内いたします。
        </span>
      </>
    ),
  },
  {
    label: '返品・キャンセルについて',
    value: (
      <>
        <span className="font-bold text-ink-900">
          デジタル商品という性質上、お支払い後のご返金・キャンセルは承っておりません。
        </span>
        <br />
        ご購入前に、30日間の無料体験にて動作および機能を必ずご確認ください。
        <br />
        <br />
        ただし、以下の場合は個別に対応いたします。
        <br />
        ・当社の責めに帰すべき事由により製品が正常に動作しない場合
        <br />
        ・ご案内した動作環境を満たしているにもかかわらずご利用いただけない場合
        <br />
        上記に該当する場合は、修正対応または返金対応をご相談させていただきます。
        まずはお問い合わせ窓口までご連絡ください。
      </>
    ),
  },
  {
    label: '動作環境',
    value: (
      <>
        Windows（デスクトップアプリケーションとしてお客様のパソコンにインストールしてご利用いただきます）
        <br />
        <span className="text-body-faint">
          ※ 本製品はお客様のパソコン内で動作します。
          会計データが外部に送信されることはありません。
        </span>
      </>
    ),
  },
  {
    label: '無料体験について',
    value: (
      <>
        30日間、すべての機能を無料でお試しいただけます。
        <br />
        お申し込みにクレジットカードのご登録は不要です。
        体験期間が終了しても自動的に課金されることはありません。
      </>
    ),
  },
];

const LegalPage: React.FC = () => (
  <LegalLayout
    eyebrow="Legal"
    title="特定商取引法に基づく表記"
    updatedAt="2026年8月30日"
    lead="特定商取引に関する法律第11条（通信販売についての広告）に基づき、以下のとおり表示いたします。"
  >
    <DefinitionTable rows={ROWS} />

    <div className="mt-10 rounded-card border border-brand-200 bg-brand-50 p-6">
      <h2 className="font-bold text-brand-800">30日間無料体験をご利用の場合</h2>
      <p className="mt-3 text-small leading-relaxed text-brand-900">
        無料体験期間中に料金が発生することはありません。
        クレジットカードのご登録も不要のため、体験期間が終了しても自動的に課金されることはありません。
        継続してご利用いただく場合のみ、担当者よりお手続きをご案内いたします。
      </p>
    </div>

    <div className="mt-8 text-small leading-relaxed text-body-soft">
      <p>
        本表記に関するご不明な点は、
        <a href="/monitor.html#form-head" className="text-brand-600 underline underline-offset-4">お申し込みフォーム</a>の「ご質問・ご要望」欄
        よりお問い合わせください。
      </p>
    </div>
  </LegalLayout>
);

export default LegalPage;
